import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Drawer, IconButton, List, ListItem, ListItemButton, ListItemText } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { logEvent } from '../../utils/ga';
import styles from './Header.module.css';

const calculatorLinks = [
  { to: '/compound', label: '복리 시뮬레이터' },
  { to: '/dividend', label: '배당 ETF 계산기' },
  { to: '/pension', label: '국민연금 계산기' },
  { to: '/retirement', label: '노후 자금 계산기' },
  { to: '/retirement/goal', label: '목표 설계 계산기' },
  { to: '/tax-benefit', label: 'ISA·IRP 세제혜택' },
  { to: '/inflation', label: '물가 상승 계산기' },
  { to: '/severance', label: '퇴직금 계산기' },
  { to: '/fire', label: 'FIRE 계산기' },
];

export const Header = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const location = useLocation();
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setMobileOpen(false);
    setDropdownOpen(false);
  }, [location]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isCalcActive = calculatorLinks.some((l) => location.pathname.startsWith(l.to));

  return (
    <header className={styles.header}>
      <div className={styles.inner}>
        <NavLink to="/" className={styles.logo}>
          노후든든
        </NavLink>

        {/* Desktop nav */}
        <nav className={styles.desktopNav}>
          <div className={styles.dropdownWrapper} ref={dropdownRef}>
            <button
              className={`${styles.navButton} ${isCalcActive ? styles.navButtonActive : ''}`}
              aria-haspopup="true"
              aria-expanded={dropdownOpen}
              onClick={() => {
                logEvent('navigation', dropdownOpen ? 'calculator_dropdown_close' : 'calculator_dropdown_open', 'header_desktop');
                setDropdownOpen((v) => !v);
              }}
            >
              계산기 <KeyboardArrowDownIcon fontSize="small" />
            </button>
            {dropdownOpen && (
              <div className={styles.dropdown} role="menu">
                {calculatorLinks.map((link) => (
                  <NavLink
                    key={link.to}
                    to={link.to}
                    role="menuitem"
                    onClick={() => logEvent('navigation', 'calculator_nav_click', link.to)}
                    className={({ isActive }) =>
                      `${styles.dropdownItem} ${isActive ? styles.dropdownItemActive : ''}`
                    }
                  >
                    {link.label}
                  </NavLink>
                ))}
              </div>
            )}
          </div>
          <NavLink
            to="/quiz"
            onClick={() => logEvent('navigation', 'quiz_nav_click', 'header_desktop')}
            className={({ isActive }) =>
              `${styles.navLink} ${isActive ? styles.navLinkActive : ''}`
            }
          >
            진단 퀴즈
          </NavLink>
          <NavLink
            to="/checklist"
            onClick={() => logEvent('navigation', 'checklist_nav_click', 'header_desktop')}
            className={({ isActive }) =>
              `${styles.navLink} ${isActive ? styles.navLinkActive : ''}`
            }
          >
            체크리스트
          </NavLink>
          <NavLink
            to="/guide"
            onClick={() => logEvent('navigation', 'guide_nav_click', 'header_desktop')}
            className={({ isActive }) =>
              `${styles.navLink} ${isActive ? styles.navLinkActive : ''}`
            }
          >
            투자가이드
          </NavLink>
        </nav>

        {/* Mobile hamburger */}
        <div className={styles.mobileMenu}>
          <IconButton
            onClick={() => {
              logEvent('navigation', 'mobile_menu_open', 'header');
              setMobileOpen(true);
            }}
            size="small"
            aria-label="메뉴 열기"
          >
            <MenuIcon />
          </IconButton>
        </div>
      </div>

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        PaperProps={{ sx: { width: 280 } }}
      >
        <div className={styles.drawerHeader}>
          <span className={styles.drawerTitle}>노후든든</span>
          <IconButton onClick={() => setMobileOpen(false)} aria-label="메뉴 닫기">
            <CloseIcon />
          </IconButton>
        </div>
        <List disablePadding>
          <ListItem disablePadding>
            <ListItemButton component={NavLink} to="/" onClick={() => logEvent('navigation', 'home_nav_click', 'header_mobile')}>
              <ListItemText primary="홈" />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding sx={{ pl: 0 }}>
            <ListItemText primary="계산기" sx={{ pl: 2, pt: 1, color: '#6b7280', fontSize: '0.75rem' }} />
          </ListItem>
          {calculatorLinks.map((link) => (
            <ListItem key={link.to} disablePadding>
              <ListItemButton
                component={NavLink}
                to={link.to}
                sx={{ pl: 3 }}
                onClick={() => logEvent('navigation', 'calculator_nav_click', `${link.to}_mobile`)}
              >
                <ListItemText primary={link.label} />
              </ListItemButton>
            </ListItem>
          ))}
          <ListItem disablePadding>
            <ListItemButton component={NavLink} to="/quiz" onClick={() => logEvent('navigation', 'quiz_nav_click', 'header_mobile')}>
              <ListItemText primary="진단 퀴즈" />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton component={NavLink} to="/checklist" onClick={() => logEvent('navigation', 'checklist_nav_click', 'header_mobile')}>
              <ListItemText primary="체크리스트" />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton component={NavLink} to="/guide" onClick={() => logEvent('navigation', 'guide_nav_click', 'header_mobile')}>
              <ListItemText primary="투자가이드" />
            </ListItemButton>
          </ListItem>
        </List>
      </Drawer>
    </header>
  );
};
